import { useEffect, useId, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  STATSCORE_EMBEDDER_SCRIPT_ID,
  STATSCORE_EMBEDDER_SCRIPT_URL,
  STATSCORE_PREMATCH_WIDGET_GROUP_ID,
  TABLES_LEAGUE_IFRAME_URL,
} from '@/domain/prematch-statistics';
import { Icon } from '@/ui/common/icon';
import { Modal } from '@/ui/common/modal';
import { Toast } from '@/ui/common/toast';
import {
  clearPrematchStatisticsRequest,
  getPrematchStatisticsRequest,
  getPrematchStatisticsRequestVersion,
  requestPrematchStatistics,
  subscribePrematchStatisticsRequest,
  type PrematchStatisticsRequest,
} from './prematch-statistics-bus';

function ensureStatscoreEmbedder(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('no-window'));
      return;
    }

    const onReady = (err?: unknown) => {
      if (err) {
        reject(err instanceof Error ? err : new Error('statscore-load-failed'));
        return;
      }
      resolve();
    };

    if (!window.STATSCOREWidgets) {
      window.STATSCOREWidgets = {
        onLoadCallbacks: [],
        onLoad(callback) {
          (this.onLoadCallbacks ??= []).push(callback);
        },
        WidgetGroup: class StatscoreWidgetGroupStub {},
      };
    }

    if (!document.getElementById(STATSCORE_EMBEDDER_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = STATSCORE_EMBEDDER_SCRIPT_ID;
      script.src = STATSCORE_EMBEDDER_SCRIPT_URL;
      script.type = 'module';
      script.async = true;
      script.addEventListener('error', () => {
        const callbacks = window.STATSCOREWidgets?.onLoadCallbacks ?? [];
        for (const callback of callbacks) callback(new Error('NetworkError'));
        onReady(new Error('NetworkError'));
      });
      document.head.appendChild(script);
    }

    window.STATSCOREWidgets.onLoad(onReady);
  });
}

/**
 * App-level host for FeedType.Standard (tablesleague iframe) and
 * FeedType.LSports (STATSCORE widget) prematch statistics, plus blocker toasts.
 */
export function PrematchStatisticsHost() {
  const { t } = useTranslation();
  const titleId = useId();
  const widgetRef = useRef<HTMLDivElement | null>(null);

  const version = useSyncExternalStore(
    subscribePrematchStatisticsRequest,
    getPrematchStatisticsRequestVersion,
    getPrematchStatisticsRequestVersion,
  );
  const request: PrematchStatisticsRequest | null =
    version > 0 ? getPrematchStatisticsRequest() : null;

  const isTablesleague = request?.kind === 'tablesleague';
  const isStatscore = request?.kind === 'statscore';
  const toastMessage = request?.kind === 'toast' ? request.message : null;

  useEffect(() => {
    if (!toastMessage) return;
    const timer = window.setTimeout(() => clearPrematchStatisticsRequest(), 4000);
    return () => window.clearTimeout(timer);
  }, [toastMessage, version]);

  useEffect(() => {
    if (!isStatscore || !request || request.kind !== 'statscore') return;
    let cancelled = false;
    widgetRef.current?.replaceChildren();

    void ensureStatscoreEmbedder()
      .then(() => {
        if (cancelled) return;
        const WidgetGroup = window.STATSCOREWidgets?.WidgetGroup;
        if (!WidgetGroup || !widgetRef.current) {
          throw new Error('missing-widget-group');
        }
        new WidgetGroup(
          widgetRef.current,
          STATSCORE_PREMATCH_WIDGET_GROUP_ID,
          { eventId: `m:${request.eventId}`, language: request.language },
          {},
        );
      })
      .catch(() => {
        if (cancelled) return;
        requestPrematchStatistics({
          kind: 'toast',
          message: t('StatisticsUnavailable', {
            defaultValue: 'Statistics are temporarily unavailable.',
          }),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isStatscore, request, t, version]);

  if (toastMessage) {
    return (
      <div className="pointer-events-none fixed inset-x-0 top-16 z-50 flex justify-center px-4">
        <div className="pointer-events-auto w-full max-w-md">
          <Toast tone="danger" onDismiss={clearPrematchStatisticsRequest}>
            {toastMessage}
          </Toast>
        </div>
      </div>
    );
  }

  if (!isTablesleague && !isStatscore) return null;

  return (
    <Modal
      open
      onClose={clearPrematchStatisticsRequest}
      aria-labelledby={titleId}
      panelClassName="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border-default bg-bg-elevated shadow-lg"
    >
      <header className="flex shrink-0 items-center justify-between border-b border-border-subtle px-4 py-3">
        <h2 id={titleId} className="text-heading-s text-text-primary">
          {isTablesleague
            ? t('LeagueTable', { defaultValue: 'League table' })
            : t('MatchStatistics', { defaultValue: 'Match statistics' })}
        </h2>
        <button
          type="button"
          onClick={clearPrematchStatisticsRequest}
          aria-label={t('Close', { defaultValue: 'Close' })}
          className="rounded-sm p-1 text-icon-muted hover:bg-bg-surface hover:text-icon-default"
        >
          <Icon name="close" size={16} />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        {isTablesleague ? (
          <iframe
            title={t('LeagueTable', { defaultValue: 'League table' })}
            src={TABLES_LEAGUE_IFRAME_URL}
            className="w-full border-0"
            style={{ height: 3000 }}
          />
        ) : (
          <div ref={widgetRef} className="min-h-[320px] w-full p-2" />
        )}
      </div>
    </Modal>
  );
}
